import { siteConfig } from "../../siteConfig";

declare global {
  interface Window {
    dataLayer: IArguments[];
    gtag: (...args: unknown[]) => void;
  }
}

type ClickEvent = {
  className: string;
  eventName: string;
  params: (el: HTMLAnchorElement) => Record<string, string>;
};

const text = (el: Element): string => el.textContent?.trim() ?? "";
const attr = (el: Element, name: string): string => el.getAttribute(name) ?? "";

// Replicates the click-event tags previously configured in the GTM container.
const clickEvents: ClickEvent[] = [
  {
    className: "menu-link",
    eventName: "MainMenuItemClick",
    params: (el) => ({ target: text(el) }),
  },
  {
    className: "social-link",
    eventName: "socialClick",
    params: (el) => ({ platform: `${text(el)}${attr(el, "data-id")}` }),
  },
  {
    className: "blog-card-link",
    eventName: "blogCardBlogLinkClick",
    params: (el) => ({ blog: attr(el, "data-id"), source: attr(el, "data-source") }),
  },
  {
    className: "blog-card-category",
    eventName: "BlogCardCategoryClick",
    // GA4 parameter names allow only letters, digits and underscores;
    // the GTM-era "target-category" could never be registered as a
    // custom dimension.
    params: (el) => ({ target_category: text(el) }),
  },
  {
    className: "blog-nav",
    eventName: "BlogNavigation",
    params: (el) => ({ action: text(el) }),
  },
  {
    className: "post-suggestion",
    eventName: "PostSuggestionClick",
    params: (el) => ({ target: text(el) }),
  },
];

const isEnabled = () =>
  typeof window !== "undefined" && process.env.NODE_ENV === "production";

const loadGtagScript = () => {
  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${siteConfig.ga4MeasurementId}`;
  document.head.appendChild(script);
};

const whenIdle = (callback: () => void) => {
  if (window.requestIdleCallback) window.requestIdleCallback(callback);
  else window.setTimeout(callback, 1500); // Safari has no requestIdleCallback
};

// The cookieconsent banner (src/html.js) runs as type "opt-in", so
// analytics must stay fully off — no stub, no listeners, no queued
// events — until the visitor explicitly allows it.
const hasConsented = () =>
  document.cookie.split("; ").includes("cookieconsent_status=allow");

let started = false;

export const initAnalytics = () => {
  if (!isEnabled()) return;

  if (hasConsented()) {
    startAnalytics();
    return;
  }

  // Dispatched by the banner's onStatusChange in src/html.js.
  window.addEventListener("cookieconsent-status-change", (event) => {
    if ((event as CustomEvent).detail !== "allow") return;
    startAnalytics();
    // The pre-consent page view was intentionally never sent; record
    // the page the visitor consented on.
    trackPageView(window.location.pathname);
  });
};

const startAnalytics = () => {
  if (started) return;
  started = true;

  // The gtag stub queues commands in the dataLayer until the real script
  // loads, so nothing fired before then is lost. gtag.js requires actual
  // `arguments` objects in the dataLayer — do not convert to arrays.
  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer.push(arguments);
  };
  window.gtag("js", new Date());
  // Page views are sent from onRouteUpdate, which also fires on first load.
  window.gtag("config", siteConfig.ga4MeasurementId, { send_page_view: false });

  document.addEventListener("click", (event) => {
    const link = (event.target as Element | null)?.closest?.("a");
    if (!link) return;
    for (const { className, eventName, params } of clickEvents) {
      if (link.classList.contains(className)) {
        window.gtag("event", eventName, params(link));
        break;
      }
    }
  });

  // Defer the ~150 KiB gtag.js download until the browser is idle after load,
  // keeping it entirely off the critical rendering path.
  if (document.readyState === "complete") {
    whenIdle(loadGtagScript);
  } else {
    window.addEventListener("load", () => whenIdle(loadGtagScript), {
      once: true,
    });
  }
};

export const trackPageView = (pathname: string) => {
  // gtag only exists once startAnalytics has run, i.e. after consent.
  if (!isEnabled() || typeof window.gtag !== "function") return;
  // Double rAF: wait until the new route's title/DOM are in place.
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      window.gtag("event", "page_view", {
        page_path: pathname,
        page_title: document.title,
        page_location: window.location.href,
      });
    });
  });
};
